import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH, PLAYER_TARGET_Y } from "../constants";
import { WOLF_FEEL_CONFIG } from "../wolfFeelConfig";
import { WOLF_REALISM_CONFIG } from "../wolfRealismConfig";
import type { WolfMovementFeel } from "./WolfStaminaSystem";
import { WolfAnimationSystem } from "./WolfAnimationSystem";

export class PlayerController {
  container: Phaser.GameObjects.Container;
  private animation: WolfAnimationSystem;
  x = GAME_WIDTH * 0.5;
  y = PLAYER_TARGET_Y;
  vx = 0;
  vy = 0;
  radius = 28;
  energy = 0;
  championTime = 0;
  lastTerrain = "dirt";
  private inputX = 0;
  private inputY = 0;
  private smoothedInputX = 0;
  private smoothedInputY = 0;
  private moving = false;
  private touchInputX = 0;
  private touchInputY = 0;
  private pointerWasDown = false;

  constructor(private scene: Phaser.Scene, layer: Phaser.GameObjects.Layer) {
    this.animation = new WolfAnimationSystem(scene, layer);
    this.container = this.animation.container;
    this.animation.reset(this.x, this.y);
  }

  reset() {
    this.x = GAME_WIDTH * 0.5;
    this.y = PLAYER_TARGET_Y;
    this.vx = 0;
    this.vy = 0;
    this.energy = 0;
    this.championTime = 0;
    this.inputX = 0;
    this.inputY = 0;
    this.smoothedInputX = 0;
    this.smoothedInputY = 0;
    this.touchInputX = 0;
    this.touchInputY = 0;
    this.pointerWasDown = false;
    this.moving = false;
    this.animation.reset(this.x, this.y);
  }

  update(dt: number, speedMultiplier: number, rearThreat: number, movementFeel?: WolfMovementFeel) {
    const rawInput = this.readInput(dt);
    const inputBlend = 1 - Math.exp(-WOLF_FEEL_CONFIG.inputSmooth * dt);
    this.smoothedInputX = Phaser.Math.Linear(this.smoothedInputX, rawInput.x, inputBlend);
    this.smoothedInputY = Phaser.Math.Linear(this.smoothedInputY, rawInput.y, inputBlend);

    const feel = movementFeel ?? {
      maxSpeed: WOLF_FEEL_CONFIG.maxSpeed,
      acceleration: WOLF_FEEL_CONFIG.acceleration,
      deceleration: WOLF_FEEL_CONFIG.deceleration,
      lateralSpeed: WOLF_FEEL_CONFIG.lateralSpeed,
      velocitySmooth: WOLF_FEEL_CONFIG.velocitySmooth,
      rotationLerp: WOLF_FEEL_CONFIG.rotationLerp,
      rotationMaxDegrees: WOLF_FEEL_CONFIG.rotationMaxDegrees,
      animationFpsMultiplier: 1,
      state: "longChase",
      stamina: WOLF_REALISM_CONFIG.maxStamina,
      worldSpeedMultiplier: 1
    } as WolfMovementFeel;
    const championBoost = this.isChampion ? 1.25 : 1;
    const maxSpeed = feel.maxSpeed * speedMultiplier * championBoost;
    const targetVx = this.smoothedInputX * maxSpeed * feel.lateralSpeed;
    const targetVy = this.smoothedInputY * maxSpeed;
    const hasInput = Math.hypot(rawInput.x, rawInput.y) > 0.05;
    const rate = hasInput ? feel.acceleration : feel.deceleration;
    const velocityBlend = 1 - Math.exp(feel.velocitySmooth * -dt);
    const blendedTargetVx = Phaser.Math.Linear(this.vx, hasInput ? targetVx : 0, velocityBlend);
    const blendedTargetVy = Phaser.Math.Linear(this.vy, hasInput ? targetVy : 0, velocityBlend);
    this.vx = this.moveToward(this.vx, blendedTargetVx, rate * dt);
    this.vy = this.moveToward(this.vy, blendedTargetVy, rate * dt);

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    const lowerBound = GAME_HEIGHT * (0.78 + rearThreat * 0.16);
    const centering = (this.isChampion ? 0.18 : 0.07) * (1 - rearThreat * 0.86);
    const clampedX = Phaser.Math.Clamp(this.x, 58, GAME_WIDTH - 58);
    const clampedY = Phaser.Math.Clamp(this.y, GAME_HEIGHT * 0.32, lowerBound);
    if (clampedX !== this.x) this.vx = 0;
    if (clampedY !== this.y) this.vy = 0;
    this.x = clampedX;
    this.y = clampedY;
    this.y = Phaser.Math.Linear(this.y, PLAYER_TARGET_Y, centering * dt);

    this.championTime = Math.max(0, this.championTime - dt);
    this.inputX = this.smoothedInputX;
    this.inputY = this.smoothedInputY;
    this.moving = hasInput || Math.hypot(this.vx, this.vy) > 24;
    this.animation.setPosition(this.x, this.y);
    this.animation.update({
      dt,
      inputX: this.inputX,
      moving: this.moving,
      speedRatio: Phaser.Math.Clamp(Math.hypot(this.vx, this.vy) / Math.max(1, feel.maxSpeed), 0, 1.2),
      champion: this.isChampion,
      movementState: feel.state,
      rotationLerp: feel.rotationLerp,
      rotationMaxDegrees: feel.rotationMaxDegrees,
      animationFpsMultiplier: feel.animationFpsMultiplier
    });
  }

  pullTowardGhostWall(dt: number, pressure: number) {
    const drag = Phaser.Math.Clamp((pressure - 0.34) / 0.66, 0, 1);
    if (drag <= 0 || this.isChampion) return;
    this.y = Phaser.Math.Clamp(this.y + (95 + drag * 250) * drag * dt, GAME_HEIGHT * 0.32, GAME_HEIGHT * 0.94);
    this.animation.setPosition(this.x, this.y);
  }

  addEnergy() {
    this.energy += 1;
    if (this.energy >= 4) {
      this.energy = 0;
      this.championTime = 5;
      return true;
    }
    return false;
  }

  get isChampion() {
    return this.championTime > 0;
  }

  get hitboxWidth() {
    return this.animation.bodyDisplaySize * WOLF_REALISM_CONFIG.collider.bodyWidthRatio;
  }

  get hitboxHeight() {
    return this.animation.bodyDisplaySize * WOLF_REALISM_CONFIG.collider.bodyHeightRatio;
  }

  get hitboxOffsetY() {
    return WOLF_FEEL_CONFIG.hitboxOffsetY;
  }

  get hitboxCenterY() {
    return this.y + this.hitboxOffsetY;
  }

  get pickupRadius() {
    return Math.max(this.hitboxWidth, this.hitboxHeight) * 0.52;
  }

  setVisualPaused(paused: boolean) {
    this.animation.setPaused(paused);
  }

  private readInput(dt: number) {
    const keyboard = this.scene.input.keyboard;
    let ix = 0;
    let iy = 0;
    if (keyboard?.addKey("A").isDown || keyboard?.addKey("LEFT").isDown) ix -= 1;
    if (keyboard?.addKey("D").isDown || keyboard?.addKey("RIGHT").isDown) ix += 1;
    if (keyboard?.addKey("W").isDown || keyboard?.addKey("UP").isDown) iy -= 1;
    if (keyboard?.addKey("S").isDown || keyboard?.addKey("DOWN").isDown) iy += 1;

    const pointer = this.scene.input.activePointer;
    if (pointer.isDown) {
      const target = this.readTouchInput(pointer);
      if (!this.pointerWasDown) {
        this.touchInputX = this.smoothedInputX;
        this.touchInputY = this.smoothedInputY;
      }
      const touchBlend = 1 - Math.exp(-WOLF_FEEL_CONFIG.inputSmooth * 0.72 * dt);
      this.touchInputX = Phaser.Math.Linear(this.touchInputX, target.x, touchBlend);
      this.touchInputY = Phaser.Math.Linear(this.touchInputY, target.y, touchBlend);
      ix = this.touchInputX;
      iy = this.touchInputY;
      this.pointerWasDown = true;
    } else {
      this.pointerWasDown = false;
      this.touchInputX = Phaser.Math.Linear(this.touchInputX, 0, 1 - Math.exp(-WOLF_FEEL_CONFIG.deceleration * 0.002 * dt));
      this.touchInputY = Phaser.Math.Linear(this.touchInputY, 0, 1 - Math.exp(-WOLF_FEEL_CONFIG.deceleration * 0.002 * dt));
    }

    const length = Math.hypot(ix, iy);
    if (length <= 0.05) return { x: 0, y: 0 };
    return { x: ix / length, y: iy / length };
  }

  private readTouchInput(pointer: Phaser.Input.Pointer) {
    const lateralRange = GAME_WIDTH * 0.32;
    const verticalRange = GAME_HEIGHT * 0.24;
    const dx = Phaser.Math.Clamp(pointer.x - this.x, -lateralRange, lateralRange);
    const dy = Phaser.Math.Clamp(pointer.y - this.y, -verticalRange, verticalRange);
    const deadZone = GAME_WIDTH * 0.028;
    if (Math.hypot(dx, dy) <= deadZone) return { x: 0, y: 0 };

    const x = Phaser.Math.Clamp(dx / lateralRange, -1, 1);
    const y = Phaser.Math.Clamp(dy / verticalRange, -1, 1);
    const length = Math.hypot(x, y);
    if (length <= 1) return { x, y };
    return { x: x / length, y: y / length };
  }

  private moveToward(current: number, target: number, maxDelta: number) {
    if (Math.abs(target - current) <= maxDelta) return target;
    return current + Math.sign(target - current) * maxDelta;
  }
}
